/**
 * Cube storage design (research/taxonomy.md §1.2): a rows × cols lattice of equal cells in an
 * outer box. A layout mode of the shared case generator; the parameter block lives in
 * `design.params`.
 */
import type { Appearance, Design } from '../../domain/schemas';
import { mm } from '../../domain/units';
import type { BaseParams, SideParams } from '../base';

export const CUBE_ID = 'cube-storage';
export const CUBE_VERSION = '1.0';
export const CUBE_LABEL = 'Cube storage';

export interface CubeParams extends BaseParams, SideParams {
  cols: number;
  rows: number;
  back: 'none' | 'overlay' | 'rebated';
  /** Egg-crate halvings between full-length members, or short dividers housed into continuous shelves. */
  intersection: 'egg-crate' | 'housed';
  /** Plinth height (ticks), 0 for none (see src/families/base.ts for `base`, feet and slats). */
  plinth: number;
}

export const DEFAULT_APPEARANCE: Readonly<Appearance> = Object.freeze({ name: 'Cube storage, my version', staging: 'studio', props: true });

export const defaultCubeParams = (): CubeParams => ({ cols: 3, rows: 3, back: 'none', intersection: 'egg-crate', plinth: 0, base: 'none', sideStyle: 'panel' });

export function cubeParamsOf(d: Pick<Design, 'params'>): CubeParams {
  return { ...defaultCubeParams(), ...((d.params ?? {}) as Partial<CubeParams>) };
}

export type CubePresetKey = 'three-by-three' | 'two-by-four' | 'bench';

export const CUBE_PRESETS: Readonly<Record<CubePresetKey, { label: string; description: string; W: number; H: number; D: number; stock: string; params: Partial<CubeParams> }>> = Object.freeze({
  'three-by-three': { label: '3 × 3 cubes 1200 × 1200', description: '1200 × 1200 × 350, egg-crate, no back', W: mm(1200), H: mm(1200), D: mm(350), stock: 'birch-ply-18', params: { cols: 3, rows: 3 } },
  'two-by-four': { label: '2 × 4 housed 800 × 1500', description: '800 × 1500 × 330, housed, overlay back', W: mm(800), H: mm(1500), D: mm(330), stock: 'birch-ply-18', params: { cols: 2, rows: 4, intersection: 'housed', back: 'overlay' } },
  bench: { label: '4 × 1 bench 1450 × 400', description: '1450 × 400 × 380, sheesham', W: mm(1450), H: mm(400), D: mm(380), stock: 'sheesham-board-25', params: { cols: 4, rows: 1 } },
});

export function createCube(key: CubePresetKey = 'three-by-three'): Design {
  const p = CUBE_PRESETS[key] ?? CUBE_PRESETS['three-by-three'];
  return {
    family: CUBE_ID, W: p.W, H: p.H, D: p.D, n: 0, even: true, openings: [],
    stockProfileId: p.stock, constructionProfileId: 'cube-storage-joints', appearance: { ...DEFAULT_APPEARANCE },
    params: { ...defaultCubeParams(), ...p.params } as unknown as Record<string, unknown>,
  };
}

export function setCubeParam<K extends keyof CubeParams>(d: Design, key: K, value: CubeParams[K]): Design {
  return { ...d, appearance: { ...d.appearance }, params: { ...cubeParamsOf(d), [key]: value } as unknown as Record<string, unknown> };
}
